import React, { useCallback, useMemo } from 'react'
import { LearningItemAsPath } from '../../models/LearningItem'
import { useTranslation } from 'react-i18next'
import styles from './CourseLearningPathPage.module.scss'
import {
  isUserAssignedToLearningItemByHimself, learningItemUserAssignmentsByOtherUser,
} from '../../state/calculations/learningItemUserAssignments'
import UserAvatar from '../../components/UserAvatar/UserAvatar'
import learningItemGroupAssignments from '../../state/calculations/learningItemGroupAssignments'
import GroupAvatar from '../../components/GroupAvatar/GroupAvatar'
import { Link } from 'react-router-dom'
import useRoutes from '../../state/useRoutes'
import { useLearningItemAsEntity } from '../../state/entities'
import Button from '../../components/basics/Button/Button'
import { useSession } from '../../state/session'
import { userCanCancelBooking } from '../../state/calculations/permissions/userCan'
import useRemoveLearningAssignmentsFromUsers from '../../state/mutations/useRemoveLearningAssignmentsFromUsers'

export default function CourseLearningPathPageEnrolledUsers({
  learningItemAsPath,
}: {
  learningItemAsPath: LearningItemAsPath
}) {
  const { t } = useTranslation()
  const routes = useRoutes()

  // We need to get all the users and groups that are assigned to this learning item.
  const { learningItem } = useLearningItemAsEntity(learningItemAsPath.uuid)

  const assignedUsers = useMemo(() => learningItemUserAssignmentsByOtherUser(learningItem), [learningItem])
  const assignedGroups = useMemo(() => learningItemGroupAssignments(learningItem), [learningItem])
  const combinedAssignments = useMemo(() => [...assignedUsers, ...assignedGroups], [assignedUsers, assignedGroups])

  const { user: currentUser } = useSession()

  const removeLearningAssignmentsFromUsers = useRemoveLearningAssignmentsFromUsers()
  const handleCancelBooking = useCallback(
    async (userUuid: string) => {
      removeLearningAssignmentsFromUsers.mutate({
        learningItemUuids: [learningItem.uuid],
        userUuids: [userUuid],
        assigningUserUuid: currentUser!.uuid,
      })
    },
    [currentUser]
  )

  return (
    <section className={styles.CourseLearningPathPageEnrolledUsers}>
      <h1>{t(`learning path.enrolled users and groups`)}</h1>

      <main>
        {combinedAssignments.length > 0 && (
          <table className={styles.enrolledUsersTable}>
            <thead>
              <tr>
                <th className={styles.avatarColumn}></th>
                <th className={styles.nameColumn}>{t('user.field name')}</th>
                <th className={styles.emailsColumn}>{t('user.emailAddress')}</th>
                <th className={styles.buttonsColumn}></th>
              </tr>
            </thead>

            <tbody>
              {combinedAssignments.map((userOrGroup) => {
                if ('firstname' in userOrGroup!) {
                  // Check if it is a user or...
                  return (
                    <tr key={userOrGroup!.uuid}>
                      <td>
                        <UserAvatar link user={userOrGroup!} />
                      </td>
                      <td>
                        <Link to={routes.manageUser.url({ uuid: userOrGroup.uuid })}>
                          {userOrGroup.firstname} {userOrGroup.lastname}
                        </Link>
                      </td>
                      <td>{userOrGroup!.email}</td>
                      <td>
                        {(userCanCancelBooking(currentUser) ||
                          (currentUser?.uuid === userOrGroup.uuid &&
                            isUserAssignedToLearningItemByHimself(userOrGroup.uuid, learningItem))) && (
                          <Button filled small onClick={() => handleCancelBooking(userOrGroup.uuid)}>
                            {t('learning item.cta cancel booking')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                } else if ('name' in userOrGroup!) {
                  // it is a group
                  return (
                    <tr key={userOrGroup!.uuid}>
                      <td>
                        <GroupAvatar group={userOrGroup!} />
                      </td>
                      <td>
                        <Link to={routes.manageGroup.url({ uuid: userOrGroup.uuid })}>{userOrGroup.name}</Link>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  )
                }
              })}
            </tbody>
          </table>
        )}

        {0 === combinedAssignments.length && (
          <div className={styles.emptyList}>{t('learning path.no entries yet')}</div>
        )}
      </main>
    </section>
  )
}
